import React, { useEffect, useState, useMemo } from 'react';
import { Modal, Button, Form, Spinner, Alert, Badge, Row, Col, Card, InputGroup } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import useToken from 'hooks/useToken';
import { useToast } from 'context/providers/ToastProvider';

/**
 * Modal para seleccionar un producto con periodicidad y generar un link de suscripción genérica.
 *
 * Flujo:
 * 1. Carga los productos activos con precios periódicos desde /api/Productos/ObtenerProductosPreciosPeriodicos
 * 2. Carga la URL base de pagos desde /api/Suscripciones/ObtenerUrlBasePagos
 * 3. Desglosa cada precio en una card independiente (si un producto tiene 4 precios, genera 4 cards)
 * 4. Permite filtrar por nombre, código de barras, periodicidad y moneda
 * 5. Al dar clic en "Generar link", empaqueta el payload encriptado en base64 (?vSub=...) y lo copia al portapapeles
 */
const ModalCrearSuscripcionGenerica = ({ show, onHide, idCliente, nombreCliente }) => {
    const navigate = useNavigate();
    const { token } = useToken();
    const { showSuccessToast, showErrorToast } = useToast();

    const handleIrACatalogo = () => {
        onHide();
        navigate('/productos/Catalogo');
    };

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [productosPrecios, setProductosPrecios] = useState([]);
    const [urlBasePagos, setUrlBasePagos] = useState('https://payments.akasia.mx/?');

    // Filtros
    const [busqueda, setBusqueda] = useState('');
    const [filtroPeriodicidad, setFiltroPeriodicidad] = useState('TODAS');

    // Estado del link generado
    const [ultimoLinkCopiado, setUltimoLinkCopiado] = useState('');
    const [copiadoId, setCopiadoId] = useState(null);

    // Cargar datos al abrir el modal
    useEffect(() => {
        if (!show) {
            setBusqueda('');
            setFiltroPeriodicidad('TODAS');
            setUltimoLinkCopiado('');
            setCopiadoId(null);
            return;
        }

        const cargarDatos = async () => {
            setLoading(true);
            setError('');
            try {
                // 1. Cargar URL Base de Pagos
                try {
                    const respUrl = await fetch('/api/Suscripciones/ObtenerUrlBasePagos', {
                        headers: { 'Authorization': `Bearer ${token}` }
                    });
                    if (respUrl.ok) {
                        const dataUrl = await respUrl.json();
                        if (dataUrl.iResponseType === 1 && dataUrl.detalles?.urlBase) {
                            setUrlBasePagos(dataUrl.detalles.urlBase);
                        }
                    }
                } catch (eUrl) {
                    console.warn('Usando URL base de pagos por defecto:', eUrl);
                }

                // 2. Cargar Catálogo de Productos con Precios Periódicos filtrados por cliente
                const endpoint = idCliente
                    ? `/api/Productos/ObtenerProductosPreciosPeriodicos?idClienteAkasia=${encodeURIComponent(idCliente)}`
                    : '/api/Productos/ObtenerProductosPreciosPeriodicos';

                const resp = await fetch(endpoint, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                });

                if (!resp.ok) {
                    throw new Error(`Error en servidor (${resp.status})`);
                }

                const data = await resp.json();
                if (data.iResponseType === 1 && data.detalles) {
                    const parsed = typeof data.detalles === 'string' ? JSON.parse(data.detalles) : data.detalles;
                    let items = [];

                    if (Array.isArray(parsed)) {
                        items = parsed;
                    } else if (parsed && typeof parsed === 'object') {
                        items = parsed.Table2 || parsed.table2 || parsed.Table1 || parsed.table1 || [];
                        if (items.length === 0) {
                            const firstArrayKey = Object.keys(parsed).find(k => Array.isArray(parsed[k]));
                            if (firstArrayKey) items = parsed[firstArrayKey];
                        }
                    }

                    console.log('Productos con periodicidad cargados:', items);
                    setProductosPrecios(items);
                } else {
                    setError(data.vMessage || 'No se pudieron cargar los productos con periodicidad.');
                }
            } catch (err) {
                setError('Error al conectar con el servidor: ' + err.message);
            } finally {
                setLoading(false);
            }
        };

        cargarDatos();
    }, [show, token, idCliente]);

    // Opciones de periodicidad dinámicas disponibles en los datos cargados
    const opcionesPeriodicidad = useMemo(() => {
        const setPer = new Set();
        productosPrecios.forEach(item => {
            if (item.vPeriodicidad) setPer.add(item.vPeriodicidad.toLowerCase());
        });
        return Array.from(setPer);
    }, [productosPrecios]);

    // Moneda del catálogo (tomada del primer producto devuelto para este cliente)
    const monedaCliente = productosPrecios[0]?.vMoneda || '';

    // Filtrar la lista de cards (por texto y periodicidad)
    const itemsFiltrados = useMemo(() => {
        const query = busqueda.trim().toLowerCase();
        return productosPrecios.filter(item => {
            // Filtro por texto: nombre o código de barras
            const matchNombre = (item.vNombreProducto || '').toLowerCase().includes(query);
            const matchCodigo = (item.vCodigoBarras || '').toLowerCase().includes(query);
            if (query && !matchNombre && !matchCodigo) return false;

            // Filtro por periodicidad
            if (filtroPeriodicidad !== 'TODAS') {
                if ((item.vPeriodicidad || '').toLowerCase() !== filtroPeriodicidad.toLowerCase()) return false;
            }

            return true;
        });
    }, [productosPrecios, busqueda, filtroPeriodicidad]);

    // Función para generar y copiar el link de pago
    const generarYCopiarLink = async (item) => {
        if (!idCliente || Number.isNaN(Number(idCliente))) {
            showErrorToast('No se puede generar el link: ID de cliente inválido.');
            return;
        }

        const payload = {
            idProductoPrecio: Number(item.idProductoPrecio),
            idClienteAkasia: Number(idCliente),
            esSuscripcion: 1
        };

        try {
            const jsonString = JSON.stringify(payload);
            const encoded = btoa(unescape(encodeURIComponent(jsonString)));
            const paymentUrl = `${urlBasePagos}vSub=${encodeURIComponent(encoded)}`;

            let copiadoExitoso = false;

            try {
                await navigator.clipboard.writeText(paymentUrl);
                copiadoExitoso = true;
            } catch {
                // Fallback para navegadores que bloqueen navigator.clipboard
                const textArea = document.createElement('textarea');
                textArea.value = paymentUrl;
                textArea.style.position = 'fixed';
                textArea.style.left = '-999999px';
                textArea.style.top = '-999999px';
                document.body.appendChild(textArea);
                textArea.focus();
                textArea.select();
                try {
                    document.execCommand('copy');
                    copiadoExitoso = true;
                } catch (execError) {
                    console.error('Error con execCommand copy:', execError);
                }
                document.body.removeChild(textArea);
            }

            if (copiadoExitoso) {
                setUltimoLinkCopiado(paymentUrl);
                setCopiadoId(item.idProductoPrecio);
                showSuccessToast('¡Link de suscripción generado y copiado al portapapeles!');
                setTimeout(() => {
                    setCopiadoId(null);
                }, 3500);
            } else {
                setUltimoLinkCopiado(paymentUrl);
                showErrorToast('No se pudo copiar automáticamente. Puedes copiar el link del recuadro superior.');
            }
        } catch (err) {
            console.error('Error al generar link de suscripción:', err);
            showErrorToast('Error al generar el link: ' + err.message);
        }
    };

    // Helper para badge de periodicidad
    const getPeriodicidadBadge = (periodicidad) => {
        const per = (periodicidad || '').toLowerCase();
        let bg = 'secondary';
        if (per.includes('mensual')) bg = 'primary';
        else if (per.includes('anual')) bg = 'success';
        else if (per.includes('semestral')) bg = 'info';
        else if (per.includes('semanal')) bg = 'warning';

        return (
            <Badge bg={bg} className="text-uppercase px-2 py-1">
                {periodicidad}
            </Badge>
        );
    };

    // Helper para badge de moneda
    const getMonedaBadge = (moneda) => {
        const mon = (moneda || 'MXN').toUpperCase();
        return (
            <Badge bg={mon === 'USD' ? 'warning' : 'teal'} className={`text-uppercase px-2 py-1 ${mon === 'USD' ? 'text-dark' : ''}`}>
                {mon}
            </Badge>
        );
    };

    return (
        <Modal show={show} onHide={onHide} size="xl" centered scrollable backdrop="static">
            <Modal.Header closeButton className="bg-light">
                <Modal.Title className="d-flex align-items-center gap-2">
                    <FontAwesomeIcon icon="sync-alt" className="text-primary" />
                    <span>Crear Suscripción Genérica</span>
                    {nombreCliente && (
                        <span className="text-muted fs--1 fw-normal ms-2">
                            Cliente: <strong>{nombreCliente}</strong> (#{idCliente})
                        </span>
                    )}
                </Modal.Title>
            </Modal.Header>

            <Modal.Body className="p-3">
                {/* Banner de último link generado */}
                {ultimoLinkCopiado && (
                    <Alert variant="success" className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center p-3 mb-3 shadow-sm border-success">
                        <div className="me-2 mb-2 mb-md-0" style={{ maxWidth: '85%' }}>
                            <div className="fw-bold d-flex align-items-center gap-2">
                                <FontAwesomeIcon icon="check-circle" className="text-success" />
                                <span>¡Link copiado al portapapeles! Listo para enviar al cliente</span>
                            </div>
                            <small className="text-break font-monospace text-muted mt-1 d-block" style={{ fontSize: '0.8rem' }}>
                                {ultimoLinkCopiado}
                            </small>
                        </div>
                        <Button
                            variant="success"
                            size="sm"
                            className="text-nowrap"
                            onClick={async () => {
                                try {
                                    await navigator.clipboard.writeText(ultimoLinkCopiado);
                                    showSuccessToast('Link vuelto a copiar al portapapeles');
                                } catch {
                                    showErrorToast('Error al copiar link');
                                }
                            }}
                        >
                            <FontAwesomeIcon icon="copy" className="me-1" /> Copiar de nuevo
                        </Button>
                    </Alert>
                )}

                {/* Barra de Filtros */}
                <Card className="mb-3 border shadow-none bg-light">
                    <Card.Body className="p-3">
                        <Row className="g-2 align-items-center">
                            {/* Búsqueda por texto */}
                            <Col xs={12} md={7}>
                                <InputGroup>
                                    <InputGroup.Text className="bg-white">
                                        <FontAwesomeIcon icon="search" className="text-muted" />
                                    </InputGroup.Text>
                                    <Form.Control
                                        type="text"
                                        placeholder="Buscar por nombre de producto o código de barras..."
                                        value={busqueda}
                                        onChange={(e) => setBusqueda(e.target.value)}
                                    />
                                    {busqueda && (
                                        <Button variant="outline-secondary" onClick={() => setBusqueda('')}>
                                            <FontAwesomeIcon icon="times" />
                                        </Button>
                                    )}
                                </InputGroup>
                            </Col>

                            {/* Filtro por Periodicidad */}
                            <Col xs={12} md={5}>
                                <Form.Select
                                    value={filtroPeriodicidad}
                                    onChange={(e) => setFiltroPeriodicidad(e.target.value)}
                                >
                                    <option value="TODAS">Periodicidad: Todas</option>
                                    {opcionesPeriodicidad.map(p => (
                                        <option key={p} value={p}>
                                            {p.charAt(0).toUpperCase() + p.slice(1)}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Col>
                        </Row>

                        {/* Resumen de resultados */}
                        <div className="d-flex justify-content-between align-items-center mt-2 pt-2 border-top">
                            <small className="text-muted">
                                Mostrando <strong>{itemsFiltrados.length}</strong> {itemsFiltrados.length === 1 ? 'opción disponible' : 'opciones disponibles'} de <strong>{productosPrecios.length}</strong> precios periódicos
                                {monedaCliente && (
                                    <span className="ms-2 badge bg-light text-dark border">
                                        Moneda: {monedaCliente}
                                    </span>
                                )}
                            </small>
                            {(busqueda || filtroPeriodicidad !== 'TODAS') && (
                                <Button
                                    variant="link"
                                    size="sm"
                                    className="p-0 text-decoration-none"
                                    onClick={() => {
                                        setBusqueda('');
                                        setFiltroPeriodicidad('TODAS');
                                    }}
                                >
                                    Limpiar filtros
                                </Button>
                            )}
                        </div>
                    </Card.Body>
                </Card>

                {/* Contenido: Cards o Estado de Carga */}
                {loading ? (
                    <div className="text-center py-5">
                        <Spinner animation="border" variant="primary" className="mb-2" />
                        <div className="text-muted">Cargando catálogo de productos con periodicidad...</div>
                    </div>
                ) : error ? (
                    <Alert variant="danger" className="text-center p-4">
                        <FontAwesomeIcon icon="exclamation-triangle" className="fs-3 mb-2" />
                        <div>{error}</div>
                    </Alert>
                ) : itemsFiltrados.length === 0 ? (
                    <div className="text-center py-4">
                        <div className="d-inline-flex justify-content-center align-items-center rounded-circle bg-light border p-3 mb-3 text-muted opacity-75" style={{ width: '60px', height: '60px' }}>
                            <FontAwesomeIcon icon="box-open" className="fs-3" />
                        </div>
                        <h5 className="fw-bold text-dark mb-1">No se encontraron productos</h5>
                        <p className="small text-muted mb-4">Intenta cambiar los términos de búsqueda o los filtros de moneda y periodicidad.</p>

                        {/* Tarjeta explicativa en GRANDE (fiel al diseño de requisitos) */}
                        <Card className="text-start border border-primary border-opacity-25 rounded-3 shadow-none mx-auto" style={{ backgroundColor: '#f0f7ff', maxWidth: '680px' }}>
                            <Card.Body className="p-4">
                                <div className="d-flex align-items-center gap-2 mb-3 text-primary">
                                    <FontAwesomeIcon icon="info-circle" className="fs-5" />
                                    <h6 className="fw-bold mb-0 text-dark" style={{ fontSize: '0.98rem' }}>
                                        Requisitos para que un producto aparezca en suscripciones:
                                    </h6>
                                </div>

                                <div className="d-flex flex-column gap-2 text-dark" style={{ fontSize: '0.88rem' }}>
                                    <div className="d-flex align-items-start gap-2">
                                        <FontAwesomeIcon icon="check" className="text-success mt-1 flex-shrink-0" />
                                        <span>
                                            <strong>Tipo de Producto:</strong> Debe estar categorizado como <span className="badge bg-light text-dark border font-monospace px-2 py-1">Otro</span>.
                                        </span>
                                    </div>
                                    <div className="d-flex align-items-start gap-2">
                                        <FontAwesomeIcon icon="check" className="text-success mt-1 flex-shrink-0" />
                                        <span>
                                            <strong>Periodicidad Habilitada:</strong> Activar el interruptor <strong>&quot;Producto con Periodicidad (Recurrente / Suscripción)&quot;</strong>.
                                        </span>
                                    </div>
                                    <div className="d-flex align-items-start gap-2">
                                        <FontAwesomeIcon icon="check" className="text-success mt-1 flex-shrink-0" />
                                        <span>
                                            <strong>Precios definidos:</strong> Marcar al menos una casilla <Badge bg="primary" className="fw-normal px-2">Mensual</Badge> o <Badge bg="primary" className="fw-normal px-2">Anual</Badge> con su precio correspondiente.
                                        </span>
                                    </div>
                                </div>

                                <div className="d-flex flex-wrap justify-content-between align-items-center pt-3 mt-3 border-top border-primary border-opacity-10 small">
                                    <span className="text-muted">
                                        ¿Necesitas crear o ajustar un producto existente?
                                    </span>
                                    <Button
                                        variant="link"
                                        size="sm"
                                        className="p-0 text-decoration-none fw-semibold text-primary"
                                        onClick={handleIrACatalogo}
                                    >
                                        Ir al Catálogo de Productos &rarr;
                                    </Button>
                                </div>
                            </Card.Body>
                        </Card>
                    </div>
                ) : (
                    <>
                        {/* Nota informativa PEQUEÑA visible siempre que hay productos cargados */}
                        <div className="alert alert-primary py-2 px-3 mb-3 d-flex align-items-center gap-2 small border border-primary border-opacity-25 rounded-3 text-dark" style={{ backgroundColor: '#f0f7ff' }}>
                            <FontAwesomeIcon icon="info-circle" className="text-primary flex-shrink-0" />
                            <span>
                                <strong>Requisitos para suscripciones:</strong> El producto debe ser tipo <Badge bg="light" text="dark" className="border px-2 py-0">Otro</Badge>, tener activado <em>&quot;Producto con Periodicidad&quot;</em> y contar con precio <Badge bg="primary" className="fw-normal px-2 py-0">Mensual</Badge> o <Badge bg="primary" className="fw-normal px-2 py-0">Anual</Badge>.
                            </span>
                        </div>

                        <Row className="g-3">
                            {itemsFiltrados.map((item) => {
                                const isCopiado = copiadoId === item.idProductoPrecio;
                                const precioTotal = parseFloat(item.dTotal || 0).toFixed(2);
                                const precioUnitario = parseFloat(item.dPrecioUnitario || 0).toFixed(2);
                                const iva = parseFloat(item.dIVA || 0).toFixed(2);
                                const moneda = (item.vMoneda || 'MXN').toUpperCase();

                                return (
                                    <Col key={item.idProductoPrecio} xs={12} sm={6} lg={4}>
                                        <Card className={`h-100 border shadow-sm transition-all ${isCopiado ? 'border-success bg-light' : 'hover-shadow'}`} style={{ transition: 'all 0.2s ease-in-out' }}>
                                            <Card.Body className="d-flex flex-column justify-content-between p-3">
                                                {/* Parte superior: Badges */}
                                                <div>
                                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                                        <div className="d-flex gap-1 align-items-center">
                                                            {getPeriodicidadBadge(item.vPeriodicidad)}
                                                            {getMonedaBadge(moneda)}
                                                        </div>
                                                        <Badge bg="light" text="dark" className="border">
                                                            #{item.idProductoPrecio}
                                                        </Badge>
                                                    </div>

                                                    {/* Nombre del producto */}
                                                    <h6 className="fw-bold mb-1 text-dark" title={item.vNombreProducto}>
                                                        {item.vNombreProducto}
                                                    </h6>

                                                    {/* Código de barras y Tipo */}
                                                    <div className="d-flex align-items-center gap-2 text-muted small mb-3">
                                                        {item.vCodigoBarras && (
                                                            <span className="font-monospace">
                                                                <FontAwesomeIcon icon="barcode" className="me-1 opacity-75" />
                                                                {item.vCodigoBarras}
                                                            </span>
                                                        )}
                                                        {item.vTipoProducto && (
                                                            <span>&bull; {item.vTipoProducto}</span>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Parte inferior: Precio y Botón de Acción */}
                                                <div className="border-top pt-2 mt-2">
                                                    <div className="d-flex justify-content-between align-items-baseline mb-1">
                                                        <span className="text-muted small">Total recurrente:</span>
                                                        <div>
                                                            <span className="fs-2 fw-bold text-primary">
                                                                ${precioTotal}
                                                            </span>
                                                            <span className="text-muted small ms-1">
                                                                {moneda} / {item.vPeriodicidad || 'ciclo'}
                                                            </span>
                                                        </div>
                                                    </div>

                                                    {/* Desglose de impuestos */}
                                                    <div className="text-end text-muted small mb-3" style={{ fontSize: '0.75rem' }}>
                                                        Subtotal: ${precioUnitario} | IVA: ${iva}
                                                    </div>

                                                    {/* Botón de Generar Link */}
                                                    <Button
                                                        variant={isCopiado ? 'success' : 'outline-primary'}
                                                        className="w-100 d-flex justify-content-center align-items-center gap-2 fw-semibold"
                                                        onClick={() => generarYCopiarLink(item)}
                                                    >
                                                        <FontAwesomeIcon icon={isCopiado ? 'check' : 'link'} />
                                                        <span>{isCopiado ? '¡Link Copiado!' : 'Generar link'}</span>
                                                    </Button>
                                                </div>
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                );
                            })}
                        </Row>
                    </>
                )}
            </Modal.Body>

            <Modal.Footer className="bg-light d-flex justify-content-between">
                <small className="text-muted">
                    <FontAwesomeIcon icon="info-circle" className="me-1" />
                    Esta suscripción no activará ni bloqueará licencias de software en el Punto de Venta.
                </small>
                <Button variant="secondary" size="sm" onClick={onHide}>
                    Cerrar
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

ModalCrearSuscripcionGenerica.propTypes = {
    show: PropTypes.bool.isRequired,
    onHide: PropTypes.func.isRequired,
    idCliente: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    nombreCliente: PropTypes.string
};

export default ModalCrearSuscripcionGenerica;
